import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Filter, X, Sliders, DollarSign, MapPin, Check, RefreshCw } from 'lucide-react';
import { getCategoryIcon } from '../../constants/icons';
import { formatNaira } from '../../utils/formatters';
import { NIGERIAN_STATES } from '../../constants/nigeria';

const DesktopSidebar = ({ categories, filters, onFilterChange, listings }) => {
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [priceRange, setPriceRange] = useState({
    min: filters.minPrice || 0,
    max: filters.maxPrice || 1000000
  });
  const [localFilters, setLocalFilters] = useState(filters);
  const navigate = useNavigate();

  const categoryTree = useMemo(() => {
    const categoryMap = {};
    const rootCategories = [];

    categories.forEach(category => {
      categoryMap[category.id] = { ...category, children: [] };
    });

    categories.forEach(category => {
      if (category.parent_id && categoryMap[category.parent_id]) {
        categoryMap[category.parent_id].children.push(categoryMap[category.id]);
      } else {
        rootCategories.push(categoryMap[category.id]);
      }
    });

    rootCategories.forEach(cat => {
      if (cat.children.length > 0) {
        cat.children.sort((a, b) => a.name.localeCompare(b.name));
      }
    });

    return rootCategories.sort((a, b) => a.name.localeCompare(b.name));
  }, [categories]);

  const { minPrice, maxPrice } = useMemo(() => {
    const listingPrices = listings
      .map(l => parseFloat(l.price))
      .filter(p => !isNaN(p));
    
    const min = listingPrices.length > 0 ? Math.min(...listingPrices) : 0;
    const max = listingPrices.length > 0 ? Math.max(...listingPrices) : 1000000;
    
    return { min, max };
  }, [listings]);

  useEffect(() => {
    setLocalFilters(filters);
    if (filters.minPrice !== null || filters.maxPrice !== null) {
      setPriceRange({
        min: filters.minPrice || minPrice,
        max: filters.maxPrice || maxPrice
      });
    }
  }, [filters, minPrice, maxPrice]);

  const handleCategorySelect = (categoryId) => {
    const newFilters = { ...localFilters, category: categoryId };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
    setHoveredCategory(null);
  };

  const handleConditionSelect = (condition) => {
    const newFilters = { ...localFilters, condition };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleLocationSelect = (locationId) => {
    const newFilters = { ...localFilters, location: locationId };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handlePriceChange = () => {
    const newFilters = { 
      ...localFilters, 
      minPrice: priceRange.min === minPrice ? null : priceRange.min,
      maxPrice: priceRange.max === maxPrice ? null : priceRange.max
    };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const newFilters = {
      category: null,
      minPrice: null,
      maxPrice: null,
      condition: 'all',
      location: null,
    };
    setLocalFilters(newFilters);
    setPriceRange({ min: minPrice, max: maxPrice });
    setHoveredCategory(null);
    onFilterChange(newFilters);
  };

  const navigateToCategory = (categoryId) => {
    navigate(`/category/${categoryId}`);
    setHoveredCategory(null);
  };

  const hasActiveFilters = 
    localFilters.category !== null ||
    localFilters.minPrice !== null ||
    localFilters.maxPrice !== null ||
    localFilters.condition !== 'all' ||
    localFilters.location !== null;

  const selectedCategory = categories.find(c => c.id === localFilters.category);

  return (
    <div className="h-full flex flex-col">
      {/* Header - Fixed height */}
      <div className="flex-shrink-0 bg-white rounded-xl shadow-sm p-4 border border-gray-100 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-2 bg-[#ff660e] rounded-lg mr-3">
              <Filter className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-bold text-gray-900 text-lg">Filters</h3>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-[#ff660e] hover:text-[#e55a0d] flex items-center font-medium"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Clear All
            </button>
          )}
        </div>

        {/* Active Filters */}
        {hasActiveFilters && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Active filters:</p>
            <div className="flex flex-wrap gap-2">
              {localFilters.category && selectedCategory && (
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-[#fff5e6] text-[#ff660e] border border-[#ffd9b3]">
                  📁 {selectedCategory.name}
                  <button
                    onClick={() => handleCategorySelect(null)}
                    className="ml-2 hover:text-[#e55a0d]"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {localFilters.condition !== 'all' && (
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-[#f0fff4] text-green-800 border border-green-200">
                  ⚙️ {localFilters.condition === 'new' ? 'New' : 'Used'}
                  <button
                    onClick={() => handleConditionSelect('all')}
                    className="ml-2 hover:text-green-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {localFilters.location && (
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-[#f5f3ff] text-purple-800 border border-purple-200">
                  📍 {NIGERIAN_STATES.find(s => s.id === localFilters.location)?.name || 'Location'}
                  <button
                    onClick={() => handleLocationSelect(null)}
                    className="ml-2 hover:text-purple-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {(localFilters.minPrice || localFilters.maxPrice) && (
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-[#fffbeb] text-yellow-800 border border-yellow-200">
                  💰 {formatNaira(localFilters.minPrice || minPrice)} - {formatNaira(localFilters.maxPrice || maxPrice)}
                  <button
                    onClick={() => handlePriceChange()}
                    className="ml-2 hover:text-yellow-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-2">
        {/* Categories Section */}
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <div className="p-2 bg-[#ff660e] rounded-lg mr-3">
              <span className="text-white">📁</span>
            </div>
            Categories
          </h4>
          
          <div className="space-y-1">
            {/* All Categories Option */}
            <button
              onClick={() => handleCategorySelect(null)}
              className={`w-full text-left px-3 py-3 rounded-xl flex items-center justify-between transition-all ${
                !localFilters.category 
                  ? 'bg-[#fff5e6] text-[#ff660e] border-2 border-[#ffd9b3]' 
                  : 'hover:bg-gray-50 hover:border-gray-200 border border-transparent'
              }`}
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-xl bg-[#ff660e] flex items-center justify-center mr-3">
                  <span className="text-white text-lg">🏬</span>
                </div>
                <span className="font-semibold">All Categories</span>
              </div>
              {!localFilters.category && (
                <div className="w-6 h-6 rounded-full bg-[#ff660e] flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>

            {/* Main Categories with Hover Submenus */}
            {categoryTree.map(category => (
              <div 
                key={category.id}
                className="relative"
                onMouseEnter={() => category.children.length > 0 && setHoveredCategory(category.id)}
                onMouseLeave={() => setHoveredCategory(null)}
              >
                <button
                  onClick={() => navigateToCategory(category.id)}
                  className={`w-full text-left px-3 py-3 rounded-xl flex items-center justify-between transition-all ${
                    localFilters.category === category.id
                      ? 'bg-[#fff5e6] text-[#ff660e] border-2 border-[#ffd9b3]' 
                      : 'hover:bg-gray-50 hover:border-gray-200 border border-transparent'
                  }`}
                >
                  <div className="flex items-center">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center mr-3 transition-transform hover:scale-110"
                      style={{ backgroundColor: category.background_color || '#DBEAFE' }}
                    >
                      <span className="text-xl">{getCategoryIcon(category.icon_name)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium block truncate">{category.name}</span>
                      {category.description && (
                        <span className="text-xs text-gray-500 block truncate">{category.description}</span>
                      )}
                    </div>
                  </div>
                  {category.children.length > 0 && (
                    <ChevronRight className="w-4 h-4 text-gray-500 ml-2" />
                  )}
                </button>

                {/* Hover Submenu - FIXED with high z-index */}
                
              </div>
            ))}
          </div>
        </div>

        {/* Price Range Filter */}
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <div className="p-2 bg-green-500 rounded-lg mr-3">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            Price Range
          </h4>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-700">Min: {formatNaira(priceRange.min)}</div>
              <div className="text-sm font-medium text-gray-700">Max: {formatNaira(priceRange.max)}</div>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm text-gray-600 mb-2">Minimum Price</label>
                <div className="relative pt-1">
                  <input
                    type="range"
                    min={minPrice}
                    max={maxPrice}
                    value={priceRange.min}
                    onChange={(e) => setPriceRange(prev => ({...prev, min: parseInt(e.target.value)}))}
                    className="w-full h-2 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#ff660e] [&::-webkit-slider-thumb]:cursor-pointer"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-600 mb-2">Maximum Price</label>
                <div className="relative pt-1">
                  <input
                    type="range"
                    min={minPrice}
                    max={maxPrice}
                    value={priceRange.max}
                    onChange={(e) => setPriceRange(prev => ({...prev, max: parseInt(e.target.value)}))}
                    className="w-full h-2 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#ff660e] [&::-webkit-slider-thumb]:cursor-pointer"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-between text-xs text-gray-500">
              <span>{formatNaira(minPrice)}</span>
              <span>{formatNaira(maxPrice)}</span>
            </div>
            
            <button
              onClick={handlePriceChange}
              className="w-full py-3 bg-[#ff660e] text-white rounded-xl font-medium hover:bg-[#e55a0d] transition-all shadow-sm hover:shadow"
            >
              Apply Price Filter
            </button>
          </div>
        </div>

        {/* Condition Filter */}
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <div className="p-2 bg-blue-500 rounded-lg mr-3">
              <Sliders className="w-5 h-5 text-white" />
            </div>
            Condition
          </h4>
          
          <div className="space-y-2">
            {[
              { value: 'all', label: 'All Conditions', icon: '🌐', color: 'bg-gray-100' },
              { value: 'new', label: 'New', icon: '🆕', color: 'bg-green-100' },
              { value: 'used', label: 'Used', icon: '🔄', color: 'bg-blue-100' },
            ].map(({ value, label, icon, color }) => (
              <button
                key={value}
                onClick={() => handleConditionSelect(value)}
                className={`w-full text-left px-3 py-3 rounded-xl flex items-center justify-between transition-all ${
                  localFilters.condition === value
                    ? 'border-2 border-[#ff660e] bg-[#fff5e6] text-gray-900'
                    : 'hover:bg-gray-50 hover:border-gray-200 border border-transparent'
                }`}
              >
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-3 ${color}`}>
                    <span className="text-lg">{icon}</span>
                  </div>
                  <span className="font-medium">{label}</span>
                </div>
                {localFilters.condition === value && (
                  <div className="w-6 h-6 rounded-full bg-[#ff660e] flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Location Filter */}
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <div className="p-2 bg-purple-500 rounded-lg mr-3">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            Location
          </h4>
          
          <div className="space-y-2 max-h-64 overflow-y-auto">
            <button
              onClick={() => handleLocationSelect(null)}
              className={`w-full text-left px-3 py-3 rounded-xl flex items-center justify-between transition-all ${
                !localFilters.location
                  ? 'bg-[#fff5e6] text-[#ff660e] border-2 border-[#ffd9b3]'
                  : 'hover:bg-gray-50 hover:border-gray-200 border border-transparent'
              }`}
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-xl bg-gray-600 flex items-center justify-center mr-3">
                  <span className="text-white text-lg">🇳🇬</span>
                </div>
                <div>
                  <span className="font-semibold block">All Nigeria</span>
                  <span className="text-xs text-gray-500">Nationwide</span>
                </div>
              </div>
              {!localFilters.location && (
                <div className="w-6 h-6 rounded-full bg-[#ff660e] flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>

            {NIGERIAN_STATES.slice(0, 10).map(state => (
              <button
                key={state.id}
                onClick={() => handleLocationSelect(state.id)}
                className={`w-full text-left px-3 py-3 rounded-xl flex items-center justify-between transition-all ${
                  localFilters.location === state.id
                    ? 'bg-[#fff5e6] text-[#ff660e] border-2 border-[#ffd9b3]'
                    : 'hover:bg-gray-50 hover:border-gray-200 border border-transparent'
                }`}
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center mr-3">
                    <MapPin className="w-5 h-5 text-purple-500" />
                  </div>
                  <span className="font-medium">{state.name}</span>
                </div>
                {localFilters.location === state.id && (
                  <div className="w-6 h-6 rounded-full bg-[#ff660e] flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopSidebar;